import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiGift, FiClock, FiCopy, FiCheck } from "react-icons/fi";

import ConfettiExplosion from "./ConfettiExplosion";
import sonidoExito from "./sounds/success.mp3";

// ─── CONFIGURACIÓN DE PREMIOS ────────────────────────────────────────────────
// Editá acá los descuentos: label (lo que se ve), pct, weight (probabilidad
// relativa: más weight = sale más seguido) y los colores del gajo.
const PREMIOS = [
  { label: "10% OFF", pct: 10, weight: 28, color: "#C6A98A", texto: "#0E0B09" },
  { label: "15% OFF", pct: 15, weight: 18, color: "#1C1410", texto: "#C6A98A" },
  { label: "5% OFF", pct: 5, weight: 26, color: "#B8977F", texto: "#0E0B09" },
  { label: "20% OFF", pct: 20, weight: 6, color: "#E8D8C3", texto: "#0E0B09" },
  { label: "10% OFF", pct: 10, weight: 16, color: "#8C6F5A", texto: "#0E0B09" },
  { label: "15% OFF", pct: 15, weight: 6, color: "#14100C", texto: "#C6A98A" },
];

const HORAS_VALIDEZ = 48; // vencimiento del código en horas
const STORAGE_KEY = "alma_ruleta_premio";
const VISTO_KEY = "alma_ruleta_visto";

// ─── HELPERS ─────────────────────────────────────────────────────────────────

// Devuelve el premio guardado si todavía está vigente; si venció, lo limpia.
export const getPremioActivo = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const premio = JSON.parse(raw);
    if (!premio || !premio.code || !premio.expiresAt) return null;
    if (Date.now() > premio.expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return premio;
  } catch {
    return null;
  }
};

const generarCodigo = (pct) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let sufijo = "";
  for (let i = 0; i < 4; i++) {
    sufijo += chars[Math.floor(Math.random() * chars.length)];
  }
  return `ALMA${pct}-${sufijo}`;
};

const elegirPremio = () => {
  const total = PREMIOS.reduce((s, p) => s + p.weight, 0);
  let r = Math.random() * total;
  for (let i = 0; i < PREMIOS.length; i++) {
    r -= PREMIOS[i].weight;
    if (r <= 0) return i;
  }
  return PREMIOS.length - 1;
};

const formatRestante = (ms) => {
  const totalSeg = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeg / 3600);
  const m = Math.floor((totalSeg % 3600) / 60);
  const s = totalSeg % 60;
  const dos = (n) => String(n).padStart(2, "0");
  return `${dos(h)}:${dos(m)}:${dos(s)}`;
};

// ─── GEOMETRÍA DE LA RUEDA (SVG) ─────────────────────────────────────────────
const CX = 150;
const CY = 150;
const R = 140;
const SEG = 360 / PREMIOS.length;

const polar = (deg, r) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
};

const segPath = (i) => {
  const [x0, y0] = polar(i * SEG, R);
  const [x1, y1] = polar((i + 1) * SEG, R);
  return `M ${CX} ${CY} L ${x0} ${y0} A ${R} ${R} 0 0 1 ${x1} ${y1} Z`;
};

// ─── COMPONENTE ──────────────────────────────────────────────────────────────
const RuletaDescuento = ({ onPremio }) => {
  const [abierto, setAbierto] = useState(false);
  const [girando, setGirando] = useState(false);
  const [rotacion, setRotacion] = useState(0);
  const [premio, setPremio] = useState(() => getPremioActivo());
  const [celebrando, setCelebrando] = useState(false);
  const [restante, setRestante] = useState("");
  const [copiado, setCopiado] = useState(false);
  const timerGiroRef = useRef(null);

  // Auto-abrir una única vez por dispositivo (a los 5s), solo si nunca giró.
  useEffect(() => {
    if (getPremioActivo()) return;
    try {
      if (localStorage.getItem(VISTO_KEY)) return;
    } catch {
      return;
    }
    const t = setTimeout(() => {
      setAbierto(true);
      try {
        localStorage.setItem(VISTO_KEY, "1");
      } catch {}
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  // Cuenta regresiva del código activo.
  useEffect(() => {
    if (!premio) return;
    const tick = () => {
      const ms = premio.expiresAt - Date.now();
      if (ms <= 0) {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {}
        setPremio(null);
        if (onPremio) onPremio(null);
        return;
      }
      setRestante(formatRestante(ms));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [premio, onPremio]);

  // Limpiar el timeout del giro si el componente se desmonta.
  useEffect(() => {
    return () => {
      if (timerGiroRef.current) clearTimeout(timerGiroRef.current);
    };
  }, []);

  const girar = () => {
    if (girando || premio) return;
    const idx = elegirPremio();
    const actual = ((rotacion % 360) + 360) % 360;
    const destino = (360 - (idx * SEG + SEG / 2)) % 360;
    const delta = 360 * 6 + ((destino - actual + 360) % 360);

    setGirando(true);
    setRotacion(rotacion + delta);
    try {
      localStorage.setItem(VISTO_KEY, "1");
    } catch {}

    timerGiroRef.current = setTimeout(() => {
      const p = PREMIOS[idx];
      const nuevo = {
        code: generarCodigo(p.pct),
        label: p.label,
        pct: p.pct,
        expiresAt: Date.now() + HORAS_VALIDEZ * 60 * 60 * 1000,
      };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(nuevo));
      } catch {}
      setPremio(nuevo);
      if (onPremio) onPremio(nuevo);
      setGirando(false);
      setCelebrando(true);
      try {
        new Audio(sonidoExito).play().catch(() => {});
      } catch {}
      setTimeout(() => setCelebrando(false), 5000);
    }, 4300);
  };

  const copiarCodigo = async () => {
    if (!premio) return;
    try {
      await navigator.clipboard.writeText(premio.code);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {}
  };

  const irAlFormulario = () => {
    setAbierto(false);
    setTimeout(() => {
      const el = document.getElementById("aplicacion");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  };

  return (
    <>
      {/* BOTÓN FLOTANTE */}
      <AnimatePresence>
        {!abierto && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            onClick={() => setAbierto(true)}
            className="fixed bottom-5 left-5 z-40
              flex items-center gap-3
              bg-[#1a1411]/95 border border-[#B8977F]/40
              text-[#C6A98A]
              pl-4 pr-5 py-3.5 rounded-full
              shadow-[0_0_30px_rgba(184,151,127,0.25)]
              backdrop-blur-xl"
          >
            <motion.span
              animate={{ rotate: [0, -12, 12, -8, 8, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 2.5 }}
            >
              <FiGift size={20} />
            </motion.span>
            <span className="uppercase tracking-[0.2em] text-[10px] font-light hidden sm:block">
              {premio ? "Tu descuento" : "Girá y ganá"}
            </span>
            {!premio && (
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#C6A98A] animate-ping" />
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* MODAL */}
      <AnimatePresence>
        {abierto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => !girando && setAbierto(false)}
            className="fixed inset-0 z-[70] bg-[#0E0B09]/90 backdrop-blur-md
              flex items-center justify-center px-4 py-8 overflow-y-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.96 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md
                bg-[#14100C] border border-[#3C2B22]/70
                px-6 py-10 sm:px-10 text-center my-auto"
            >
              {/* CERRAR */}
              <button
                type="button"
                onClick={() => !girando && setAbierto(false)}
                className="absolute top-4 right-4 text-[#8C6F5A] hover:text-[#C6A98A]
                  transition-colors duration-300 disabled:opacity-40"
                disabled={girando}
              >
                <FiX size={18} />
              </button>

              {/* HEADER */}
              <p className="uppercase text-[#8C6F5A] tracking-[0.35em] text-[10px] font-light mb-3">
                Promo lanzamiento · Mentorías
              </p>
              <h3 className="uppercase text-[#C6A98A] font-light text-2xl tracking-[0.18em] mb-2">
                Ruleta ALMA
              </h3>
              <p className="text-[#D4BCAA] text-sm font-light italic leading-relaxed mb-8">
                {premio
                  ? "Este es tu descuento para la mentoría 1:1."
                  : "Un solo giro. Ganá un descuento para tu inscripción a la mentoría 1:1."}
              </p>

              {/* RUEDA */}
              <div className="relative w-[260px] h-[260px] sm:w-[300px] sm:h-[300px] mx-auto mb-8">
                {/* Puntero */}
                <div
                  className="absolute left-1/2 -top-1 -translate-x-1/2 z-10
                    w-0 h-0 border-l-[10px] border-r-[10px] border-t-[16px]
                    border-l-transparent border-r-transparent border-t-[#E8D8C3]
                    drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]"
                />
                <motion.div
                  animate={{ rotate: rotacion }}
                  transition={{ duration: 4, ease: [0.12, 0.75, 0.15, 1] }}
                  className="w-full h-full"
                >
                  <svg viewBox="0 0 300 300" className="w-full h-full">
                    {PREMIOS.map((p, i) => (
                      <path
                        key={i}
                        d={segPath(i)}
                        fill={p.color}
                        stroke="#0E0B09"
                        strokeWidth="2"
                      />
                    ))}
                    {PREMIOS.map((p, i) => {
                      const mid = i * SEG + SEG / 2;
                      const [lx, ly] = polar(mid, 95);
                      return (
                        <text
                          key={`t${i}`}
                          x={lx}
                          y={ly}
                          transform={`rotate(${mid} ${lx} ${ly})`}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill={p.texto}
                          style={{
                            fontSize: "15px",
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                          }}
                        >
                          {p.label}
                        </text>
                      );
                    })}
                    <circle
                      cx={CX}
                      cy={CY}
                      r="24"
                      fill="#0E0B09"
                      stroke="#B8977F"
                      strokeWidth="2"
                    />
                    <text
                      x={CX}
                      y={CY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#C6A98A"
                      style={{ fontSize: "11px", letterSpacing: "0.15em" }}
                    >
                      ALMA
                    </text>
                  </svg>
                </motion.div>
              </div>

              {/* ESTADO: SIN PREMIO, BOTÓN GIRAR */}
              {!premio && (
                <button
                  type="button"
                  onClick={girar}
                  disabled={girando}
                  className="w-full py-4 bg-[#B8977F] text-[#0E0B09]
                    uppercase tracking-[0.3em] text-sm
                    hover:bg-transparent hover:text-[#EDE0D0]
                    border border-[#B8977F] transition duration-500
                    disabled:opacity-60 disabled:cursor-wait"
                >
                  {girando ? "Girando..." : "Girar la ruleta"}
                </button>
              )}

              {/* ESTADO: PREMIO GANADO */}
              {premio && (
                <div className="flex flex-col gap-4">
                  <p className="uppercase text-[#E8D8C3] tracking-[0.25em] text-lg font-light">
                    ¡Ganaste {premio.label}!
                  </p>

                  {/* CÓDIGO */}
                  <button
                    type="button"
                    onClick={copiarCodigo}
                    className="group flex items-center justify-center gap-3
                      border border-dashed border-[#B8977F]/60 bg-[#B8977F]/10
                      px-6 py-4"
                    title="Copiar código"
                  >
                    <span className="text-[#EDE0D0] text-xl tracking-[0.2em] font-light">
                      {premio.code}
                    </span>
                    {copiado ? (
                      <FiCheck className="text-[#C6A98A]" />
                    ) : (
                      <FiCopy className="text-[#8C6F5A] group-hover:text-[#C6A98A] transition-colors" />
                    )}
                  </button>

                  {/* VENCIMIENTO */}
                  <div className="flex items-center justify-center gap-2 text-[#8C6F5A] text-xs tracking-[0.15em] uppercase">
                    <FiClock />
                    <span>
                      Vence en <span className="text-[#C6A98A]">{restante}</span>
                    </span>
                  </div>

                  <p className="text-[#D4BCAA] text-xs font-light italic leading-relaxed">
                    Tu código se adjunta automáticamente cuando envías tu
                    aplicación a la mentoría.
                  </p>

                  <button
                    type="button"
                    onClick={irAlFormulario}
                    className="w-full py-4 bg-[#B8977F] text-[#0E0B09]
                      uppercase tracking-[0.3em] text-xs
                      hover:bg-transparent hover:text-[#EDE0D0]
                      border border-[#B8977F] transition duration-500"
                  >
                    Completar mi aplicación
                  </button>
                </div>
              )}

              {/* LETRA CHICA */}
              <p className="mt-6 text-[#7A5E50] text-[10px] tracking-[0.12em] uppercase leading-relaxed">
                Un giro por persona · Válido {HORAS_VALIDEZ}hs · Aplica a las
                primeras inscripciones
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CONFETTI */}
      {celebrando && <ConfettiExplosion cantidad={90} />}
    </>
  );
};

export default RuletaDescuento;
